/*
 *  -----------------------------------------------------------------------
 *    <copyright file="two-factor-phone.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useBoolean } from 'hooks';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons/button';
import { DialogActions, DialogContent } from 'views/components/core/dialog';
import DialogInformationBox from 'views/components/core/dialog-information-box/dialog-information-box';
import FormControl from 'views/components/core/form-control';
import Loading from 'views/components/core/loading';

import 'views/layouts/login/login.scss';
import 'views/pages/uam/uam-styles.scss';
import CustomPhoneInput from 'views/components/core/phone-input';

const TwoFactorPhone = ({ submitTwoFactorPhone }: { submitTwoFactorPhone: Function }) => {
    const { t } = useTranslation();
    const loading = useBoolean(false);
    const disabledConfirm = useBoolean(true);
    const isDisabledBtn = useBoolean(false);
    const [phoneError, setPhoneError] = useState(null);
    const [confirmedPhoneError, setConfirmedPhoneError] = useState(null);
    const [country, setCountry] = useState(null);

    const [errors, setErrors] = useState({} as any);
    const [mobileNumber, setMobileNumber] = useState({
        phone: '' as string,
        confirmedPhone: '' as string,
    } as any);

    useEffect(() => {
        const isDisabled =
            mobileNumber.phone.length <= country?.dialCode.length ||
            mobileNumber.confirmedPhone.length <= country?.dialCode.length ||
            mobileNumber.phone !== mobileNumber.confirmedPhone;
        isDisabledBtn.setValue(isDisabled);

        if (mobileNumber.phone !== mobileNumber.confirmedPhone)
            setErrors({
                phone: t('uam:fieldsShouldBeIdentical'),
                confirmedPhone: t('uam:fieldsShouldBeIdentical'),
            });
    }, [mobileNumber.phone, mobileNumber.confirmedPhone, disabledConfirm.value, country]);

    const submitTwoFactorAuth = async (e: any) => {
        e.preventDefault();
        disabledConfirm.setTrue();
        submitTwoFactorPhone(mobileNumber.confirmedPhone);
    };

    const validateOnUpdate = () => {
        const localErrors: { [key: string]: any } = {};
        const errorPhone = validateFields(mobileNumber.phone, 'phone', 16, true);
        const errorConfirmedPhone = validateFields(mobileNumber.confirmedPhone, 'confirmedPhone', 16, true);

        localErrors.phone = errorPhone.phone ? t('uam:cellMobileFieldIsRequired') : null;
        localErrors.confirmedPhone = errorConfirmedPhone.confirmedPhone ? t('uam:cellMobileFieldIsRequired') : null;
        if (!localErrors.phone && confirmedPhoneError) {
            if (confirmedPhoneError || phoneError) {
                localErrors.phone = phoneError;
                localErrors.confirmedPhone = confirmedPhoneError;
            } else if (mobileNumber.phone !== mobileNumber.confirmedPhone) {
                localErrors.phone = t('uam:fieldsShouldBeIdentical');
                localErrors.confirmedPhone = t('uam:fieldsShouldBeIdentical');
            }
        }

        setErrors({
            ...errors,
            ...localErrors,
        });

        return Object.keys(localErrors).filter((item) => localErrors[item]).length > 0;
    };

    const onSubmit = async (e: any) => {
        disabledConfirm.setTrue();
        const hasErrors = validateOnUpdate();

        if (!hasErrors) await submitTwoFactorAuth(e);
    };

    const onChange = (name: string) => (event: any) => {
        setErrors({
            ...errors,
            phone: null,
            confirmedPhone: null,
        });

        disabledConfirm.setFalse();
        setMobileNumber({
            ...mobileNumber,
            [name]: event.target.value,
        });
    };

    const setIsValidPhone = (value: string | null) => {
        setPhoneError(value);
    };

    const setIsValidConfirmedPhone = (value: string | null) => {
        setConfirmedPhoneError(value);
    };

    if (loading.value) return <Loading />;

    return (
        <div className="login-page">
            <h1 className="login-title">{t('login:twoFactorSetup')}</h1>

            <DialogInformationBox topText={t('login:twoFactorSetupMessage')} />

            <DialogContent className="login-page-form">
                <FormControl className="form-control mb error" fullWidth required>
                    <div className="two-factor-phone z-index-2">
                        <CustomPhoneInput
                            customId="two-factor-phone"
                            phone={mobileNumber.phone || ''}
                            error={errors.phone}
                            label={`${t('uam:phone')}*`}
                            isValid={setIsValidPhone}
                            onChange={(event: string | null | undefined, country: any) => {
                                const newEvent = { target: { value: event } };
                                onChange('phone')(newEvent);
                                setCountry(country);
                            }}
                        />
                    </div>
                </FormControl>

                <FormControl className="form-control mb error" fullWidth required>
                    <div className="two-factor-phone margin-b z-index-1">
                        <CustomPhoneInput
                            customId="confirm-two-factor-phone"
                            phone={mobileNumber.confirmedPhone || ''}
                            error={errors.confirmedPhone}
                            label={`${t('uam:confirmPhone')}*`}
                            isValid={setIsValidConfirmedPhone}
                            onChange={(event: string | null | undefined) => {
                                const newEvent = { target: { value: event } };
                                onChange('confirmedPhone')(newEvent);
                                setCountry(country);
                            }}
                            dropdownClass="max-height-100"
                        />
                    </div>
                </FormControl>

                <DialogActions style={{ paddingTop: 20, paddingBottom: 10 }}>
                    <Button
                        className="login-submit-button"
                        disabled={disabledConfirm.value || isDisabledBtn.value}
                        text="login:completeSignIn"
                        id="two-factor-signin"
                        type="submit"
                        onClick={onSubmit}
                    />
                </DialogActions>
            </DialogContent>
        </div>
    );
};

export default TwoFactorPhone;
